import { vec2 } from 'gl-matrix'
import Draggable from 'react-draggable'
import {Connection, Port, RootState } from ".."
import { SVGEditorGlobals, View } from './SVGEditor'
import produce from 'immer'
import { Barrier } from '../Utils/Barrier'
import RBush from 'rbush'
import { Geometry } from '../Utils/Geometry'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { ColorScheme, lighten, shades } from '../Utils/Color'
import convert from 'color-convert'
import { nanoid } from '@reduxjs/toolkit'
import { useGesture } from '@use-gesture/react'

export { PortSVGViewer, PortSVGShape }

function PortSVGViewer(props: {
  port: Port,
  connection: Connection
  onChange: (port: Port) => void
  hover: boolean
  setHover: (hover: boolean) => void
  globals: SVGEditorGlobals
}) {
  const cancelGesture = useGesture({
    onDrag: state => { state.event.stopPropagation() }
  }, {})
  
  const changePosition = (point: [number, number]) => {
    const port = produce(props.port, (draft: Port) => {
      draft.position = point
    })
    props.onChange(port)
  }

  const changeDirection = (direction: [number, number]) => {
    const port = produce(props.port, (draft: Port) => {
      draft.direction = direction
    })
    props.onChange(port)
  }

  const changePort = (port: Port) => {
    const changed = produce(port, (draft: Port) => {
    })
    props.onChange(changed)
  }

  const [preview, setPreview] = useState({
    isActive: false,
    port: props.port
  } as {
    isActive: boolean,
    port: Port
  })

  const portPosition = props.port.position
  const portDirection = vec2.scale([0, 0], props.port.direction, 1 / vec2.length(props.port.direction))
  const anchorPosition = vec2.add([0, 0], portPosition, vec2.scale([0, 0], portDirection, 1.5 * props.connection.width))

  const settings = useSelector((state: RootState) => state.settings)
  const colorScheme = shades(props.connection.color ?? settings.connectionDefaultColor)
  const selected = props.globals.selection.selected === props.connection.id

  return <g
      onClick={(e) => {
        selected ? props.globals.selection.setSelected(''): props.globals.selection.setSelected(props.connection.id)
        e.stopPropagation()
      }}
    >
    <PortSVGShape
      id={`${props.connection.id}_${props.connection.ports.indexOf(props.port)}`}
      port={props.port}
      portWidth={props.connection.width}
      colorScheme={colorScheme}
      highlighted={props.hover || selected}
      preview={false}
    ></PortSVGShape>

    { preview.isActive && <PortSVGShape
      id={`${props.connection.id}_${props.connection.ports.indexOf(props.port)}_preview`}
      port={preview.port}
      portWidth={props.connection.width}
      colorScheme={colorScheme}
      highlighted={false}
      preview={true}
    ></PortSVGShape>
    }
    { props.globals.selection.selected === props.connection.id &&
      <React.Fragment>
      <Draggable
        scale={props.globals.view.scale}
        position={{x: props.port.position[0], y: props.port.position[1]}}
        onDrag={(_, d) => {
          const port = Geometry.findMovedPort(preview.port, [d.x, d.y], settings.positionInteractionPointRadius / props.globals.view.scale, props.globals.barriers)
          setPreview({
            isActive: true,
            port: port
          })
        }}
        onStop={(_, d) => {
          const port = Geometry.findMovedPort(props.port, [d.x, d.y], settings.positionInteractionPointRadius / props.globals.view.scale, props.globals.barriers)
          changePort(port)
          setPreview({
            isActive: false,
            port: port
          })
        }}
      >
        <circle
          {...cancelGesture()}
          className='position-interactionpoint'
          cx={0}
          cy={0}
          r={settings.positionInteractionPointRadius / props.globals.view.scale}
          onClick={(e) => e.stopPropagation()}
        >
        </circle>
      </Draggable>
      <Draggable
        scale={props.globals.view.scale}
        position={{x: anchorPosition[0], y: anchorPosition[1]}}
        onDrag={(_, d) => {
          setPreview({
            isActive: true,
            port: produce(props.port, (draft: Port) => {
              draft.direction = [d.x - portPosition[0], d.y - portPosition[1]]
            })
          })
        }}
        onStop={(_, d) => {
          changeDirection([d.x - portPosition[0], d.y - portPosition[1]])
          setPreview({
            isActive: false,
            port: props.port
          })
        }}
      >
        <circle
          {...cancelGesture()}
          className='interactionpoint'
          cx={0}
          cy={0}
          r={settings.interactionPointRadius / props.globals.view.scale}
          onClick={(e) => e.stopPropagation()}
        >
        </circle>
      </Draggable>
      </React.Fragment>
    }
  </g>
}

function PortSVGShape(props: {
  id: string
  port: Port
  portWidth: number
  colorScheme: ColorScheme
  highlighted: boolean
  preview: boolean
}) {

  const portPosition = props.port.position
  const widthHalf = 0.5 * props.portWidth
  const portDirection = vec2.scale([0, 0], props.port.direction, 1 / vec2.length(props.port.direction))
  const anchorPosition = vec2.add([0, 0], portPosition, vec2.scale([0, 0], portDirection, 1.5 * props.portWidth))
  const lineDirection: vec2 = [-portDirection[1], portDirection[0]]
  const linePointOffset = vec2.scale([0, 0], lineDirection, widthHalf)
  const linePoints = [vec2.add([0, 0], portPosition, linePointOffset), vec2.add([0, 0], portPosition, vec2.scale([0, 0], linePointOffset, -1))]
  const cap = vec2.add([0, 0], portPosition, vec2.scale([0, 0], portDirection, widthHalf))
  const points = [...linePoints, cap]
  const ppoints = points.map(p => `${Number.isNaN(p[0]) ? 0 : p[0]},${Number.isNaN(p[1]) ? 0 : p[1]}`).join(' ')
  const outerPoints = [vec2.add([0, 0], anchorPosition, linePointOffset), vec2.add([0, 0], anchorPosition, vec2.scale([0, 0], linePointOffset, -1))]
  const gpoints = [linePoints[0], outerPoints[0], outerPoints[1], linePoints[1]].map(p => `${Number.isNaN(p[0]) ? 0 : p[0]},${Number.isNaN(p[1]) ? 0 : p[1]}`).join(' ')

  return <g
    style={{
      opacity: props.preview ? 0.5 : 1
    }}
  >
  <linearGradient
    id={props.id}
    x1={portPosition[0]}
    y1={portPosition[1]}
    x2={anchorPosition[0]}
    y2={anchorPosition[1]}
    gradientUnits="userSpaceOnUse"
    style={{
      transition: 'all 0.15s ease-in'
    }}
  >
    <stop
      offset="20%"
      stopColor={props.highlighted ? props.colorScheme.highlightColor : props.colorScheme.color}
      stopOpacity="0.8"
      style={{
        transition: 'all 0.15s ease-in'
      }}
    />
    <stop
      offset="100%"
      stopColor={props.highlighted ? props.colorScheme.highlightColor : props.colorScheme.color}
      stopOpacity="0.01"
      style={{
        transition: 'all 0.15s ease-in'
      }}
    />
  </linearGradient>
  <polygon
    points={gpoints}
    style={{
      fill: `url(#${props.id})`,
      opacity: props.highlighted ? 0.8 : 1,
    }}
  ></polygon>
  <polygon
    points={ppoints}
    style={{
      fill: props.highlighted ? props.colorScheme.highlightColor : props.colorScheme.color,
      opacity: props.highlighted ? 0.8 : 1,
      strokeLinejoin: 'round',
      strokeWidth: props.highlighted ? 2 : 0,
      stroke: props.highlighted ? props.colorScheme.highlightBorder : props.colorScheme.color,
      transition: 'all 0.15s ease-in'
    }}
  >
  </polygon>
  </g>
}
